import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { Suspense } from 'react';

// Layout components
import MainLayout from './components/layout/MainLayout';
import { LoadingState } from './components/common/LoadingState';
import { ThemeProvider } from './contexts/ThemeContext';

// Pages (lazy loaded)
const Dashboard = React.lazy(() => import('./pages/Dashboard'));
const Login = React.lazy(() => import('./pages/Login'));
const Integrations = React.lazy(() => import('./pages/Integrations'));
const ProjectGroupAnalysis = React.lazy(() => import('./pages/ProjectGroupAnalysis'));
const SecurityReport = React.lazy(() => import('./pages/SecurityReport'));
const EmployeeCompliance = React.lazy(() => import('./pages/EmployeeCompliance'));
const GroupMapping = React.lazy(() => import('./pages/GroupMapping'));
const Settings = React.lazy(() => import('./pages/Settings'));
const SoftwareValidation = React.lazy(() => import('./pages/SoftwareValidation'));
const SoftwareValidationDetail = React.lazy(() => import('./pages/SoftwareValidationDetail'));
const SupplierQualificationDetail = React.lazy(() => import('./pages/SupplierQualificationDetail'));

// Protected route wrapper
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const isAuthenticated = useIsAuthenticated();
  const { inProgress } = useMsal();

  if (inProgress !== InteractionStatus.None) {
    return <LoadingState />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

function App() {
  const isAuthenticated = useIsAuthenticated();
  const { inProgress } = useMsal();

  if (inProgress !== InteractionStatus.None) {
    return <LoadingState />;
  }

  return (
    <ThemeProvider defaultTheme="system" storageKey="owt-ui-theme">
      <div className="min-h-screen bg-background text-foreground">
        <Suspense fallback={<LoadingState />}>
          <Routes>
            {/* Public routes */}
            <Route
              path="/login"
              element={
                isAuthenticated ? (
                  <Navigate to="/" replace />
                ) : (
                  <Login />
                )
              }
            />

            {/* Protected routes */}
            <Route
              path="/*"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <Routes>
                      <Route path="/" element={<Dashboard />} />
                      <Route path="/dashboard" element={<Navigate to="/" replace />} />
                      <Route path="/integrations" element={<Integrations />} />
                      <Route path="/project-analysis" element={<ProjectGroupAnalysis />} />
                      <Route path="/group-mapping" element={<GroupMapping />} />
                      <Route path="/weekly-report" element={<SecurityReport />} />
                      <Route path="/security-report" element={<SecurityReport />} />
                      <Route path="/employee-compliance" element={<EmployeeCompliance />} />
                      <Route path="/settings" element={<Settings />} />
                      <Route path="/software-validation" element={<SoftwareValidation />} />
                      <Route path="/software-validation/suppliers/:id" element={<SupplierQualificationDetail />} />
                      <Route path="/software-validation/:id" element={<SoftwareValidationDetail />} />
                      <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                  </MainLayout>
                </ProtectedRoute>
              }
            />
          </Routes>
        </Suspense>
      </div>
    </ThemeProvider>
  );
}

export default App;